// The document object the engine consumes, and the findings it produces.
// Produced entirely in the browser — nothing here is ever sent to a server.

// Type-only, and therefore erased: the reconciliation check owns the shape of
// the selection a reviewer confirms, and the findings record stores it.
import type { ReconcileSelection } from '../engine/deterministic/reconciliation';

/** The fifteen failure modes in reference/dispute-taxonomy.json. */
export type FailureModeId =
  | 'FM-01'
  | 'FM-02'
  | 'FM-03'
  | 'FM-04'
  | 'FM-05'
  | 'FM-06'
  | 'FM-07'
  | 'FM-08'
  | 'FM-09'
  | 'FM-10'
  | 'FM-11'
  | 'FM-12'
  | 'FM-13'
  | 'FM-14'
  | 'FM-15';

/**
 * Tier 1: a named federal audit documents this exact cause producing a real
 * dispute or dollar loss. Tier 2: the standard warns about it, but no audit case
 * is attached yet. This is the only ranking in the system — there are no
 * severity or confidence scores.
 */
export type Tier = 1 | 2;

export type EngineLayer = 'deterministic' | 'model-assisted' | 'join';

export interface SourceDocument {
  id: string;
  fileName: string;
  pageCount: number;
  rawText: string;
  pages: { number: number; text: string }[];
  sections: Section[];
  tables: ExtractedTable[];
  citations: Citation[];
  metadata: { awardDate?: string; idiqGeneration?: 1 | 2 | 3 | 4 };
  ingestedAt: string;
  /** Non-fatal observations about the parse. Empty on a clean ingest. */
  warnings?: IngestWarning[];
}

export interface Section {
  id: string;
  /** Dotted number as printed, e.g. "3" or "3.2.1". */
  number: string;
  title: string;
  startPage: number;
  endPage: number;
  text: string;
}

export interface ExtractedTable {
  id: string;
  /** As printed, e.g. "Table 3-2". */
  label: string;
  page: number;
  caption: string;
  headers: string[];
  rows: string[][];
  numericCells: { row: number; col: number; value: number; raw: string }[];
  /**
   * Table extraction from PDF is imperfect. A low-confidence table is still
   * emitted — never silently dropped — and carries the reasons it is doubted.
   */
  confidence: 'high' | 'low';
  confidenceNotes: string[];
}

export interface Citation {
  id: string;
  raw: string;
  page: number;
  kind: 'standard' | 'url' | 'method-code' | 'internal';
  versionGiven: boolean;
  dateGiven: boolean;
  resolvedUrl?: string;
  /** Unresolvable stub: too short, or containing "…", "www…", "TBD", "[". */
  placeholder: boolean;
}

/** Why an ingest was refused, so the UI can say something specific. */
export type IngestRejectionReason =
  | 'not-pdf'
  | 'too-large'
  | 'no-text-layer'
  | 'corrupt'
  | 'encrypted'
  | 'unreadable';

export interface IngestRejection {
  reason: IngestRejectionReason;
  message: string;
  detail?: string;
}

/**
 * Something worth telling the user about a document that was ingested
 * successfully. A warning is not a refusal: the checks still run. Refusing a
 * readable document because its headings are unnumbered would be worse than
 * running on it and saying so.
 */
export type IngestWarningReason = 'no-sections' | 'no-tables' | 'thin-text';

export interface IngestWarning {
  reason: IngestWarningReason;
  message: string;
  detail: string;
  /** What the user should do about it. */
  action: string;
}

export interface IngestProgress {
  phase: 'reading' | 'extracting' | 'parsing' | 'done';
  page?: number;
  pageCount?: number;
  message: string;
}

export type IngestResult =
  | { ok: true; document: SourceDocument }
  | { ok: false; rejection: IngestRejection };

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
/** Below this many extracted characters a PDF is treated as image-only. */
export const MIN_TEXT_CHARS = 500;

/* ==========================================================================
   Findings
   ========================================================================== */

/**
 * The receipt is what makes a deterministic flag verifiable: the inputs and the
 * operation, so a practitioner can redo the check on paper and either confirm
 * it or catch us being wrong. A deterministic flag without one is a bug.
 */
export interface Receipt {
  kind: 'arithmetic' | 'lookup' | 'resolution' | 'checklist';
  inputs: { label: string; value: string }[];
  computation?: string;
  result: string;
}

/**
 * Which part of the plan a finding is about, as a reviewer would name it,
 * rather than how the finding was produced. The mapping from failure mode to
 * plan area lives in reference/dispute-taxonomy.json; the labels live in
 * src/data/planAreas.ts.
 */
export type PlanAreaId =
  | 'savings-arithmetic'
  | 'baseline-and-witnessing'
  | 'assumptions-and-stipulations'
  | 'adjustments-and-what-happens-later'
  | 'om-and-budget'
  | 'data-of-record-and-disputes'
  | 'references-and-definitions';

/**
 * Where a quoted span sits, so the document pane can highlight it.
 *
 * start and end are character offsets into that page's own text, not into
 * doc.rawText, because the pane renders one page at a time. A quote that could
 * not be located, or that runs across a page break and so has no single page to
 * sit on, is recorded as null. The pane then says it cannot show the span,
 * which is honest; highlighting an approximate span would not be.
 */
export interface QuoteOffsets {
  /** 1-based, matching SourceDocument.pages[].number. */
  page: number;
  start: number;
  /** Exclusive. */
  end: number;
}

/**
 * A confirmation a finding is still waiting on.
 *
 * The finding stands without it — its own arithmetic is complete — but one
 * further check could run against it if the reviewer answered one more
 * question. It rides on the finding rather than being inferred by the workspace
 * so that the ask appears inside the finding that needs it, and only there.
 */
export interface PendingAnswer {
  /** Which question. Today there is one. */
  question: 'rate-table';
  /** Why answering it is worth the reviewer's time, in their language. */
  prompt: string;
}

export interface Flag {
  id: string;
  failureModeId: FailureModeId;
  tier: Tier;
  layer: 'deterministic' | 'model-assisted';
  /** Which check produced this, e.g. "citation-check". */
  checkId: string;
  title: string;
  /** Verbatim substring of doc.rawText. Null only where the finding is absence. */
  quote: string | null;
  sectionRef: string;
  page: number | null;
  /** Which taxonomy detection_signal matched. */
  basis: string;
  evidenceCaseIds: string[];
  /** Required on every deterministic flag. */
  receipt?: Receipt;
  status: 'open' | 'confirmed' | 'dismissed';
  userNote?: string;

  /* --- Presentation. Derived once by enrichFlag(), never by a renderer. --- */

  /** Which part of the plan this is about. Findings are grouped by this. */
  planArea: PlanAreaId;
  /**
   * The one-sentence plain-language claim shown as layer 1, before any code,
   * tier or matrix reference is visible. Defaults to the failure mode's plain
   * title; a check may narrow it to what it actually found.
   */
  claim: string;
  /** One plain-language sentence of context beneath the claim. */
  contextLine: string;
  /**
   * The draft review comment, in a reviewer's voice, for the P3-09 comment
   * sheet. An observation and a question: never proposed contract language,
   * never an assertion of who is right, never a severity. Always editable
   * before export.
   */
  commentDraft: string;
  /** Where to highlight the quote, or null when it cannot be located. */
  quoteOffsets: QuoteOffsets | null;
  /** One further confirmation this finding could still use. Usually absent. */
  pendingAnswer?: PendingAnswer;
}

/**
 * What a check emits. planArea, claim, contextLine and commentDraft are
 * derived centrally by enrichFlag() so a finding's plain-language surface
 * cannot drift between one check and another. quoteOffsets is not derived:
 * only the check that found the span knows where it was looking.
 */
export type FlagDraft = Omit<
  Flag,
  'planArea' | 'claim' | 'contextLine' | 'commentDraft'
> & {
  /** Overrides the failure mode's plain title where the check can be specific. */
  claim?: string;
  /** Overrides the failure mode's plain description, likewise. */
  contextLine?: string;
};

/* ==========================================================================
   The review
   --------------------------------------------------------------------------
   A finding is what the engine produced. A review is what the reviewer did
   with it: a decision on each finding, the comment they will actually send,
   and the header block of the FEMP P3-09 comment sheet those comments go on.

   Every decision has to have a consequence downstream, so a decision is a
   record, not a UI state. Nothing here is transmitted anywhere; it lives in
   the reviewer's own browser.
   ========================================================================== */

/**
 * Why a finding was set aside. Captured on "not a concern" so the decision can
 * be defended later, and so a reason that keeps recurring can quiet a check
 * that does not apply to this reviewer's work. Never a judgement about the
 * finding's importance — there is no severity in this product.
 */
export type DismissReason =
  | 'addressed-elsewhere'
  | 'standard-practice'
  | 'not-applicable'
  | 'misread'
  | 'not-worth-a-comment'
  | 'other';

/**
 * A reviewer's decision on one finding.
 *
 * 'include' puts the comment on the sheet. 'not-a-concern' takes it off and
 * records why. 'refer' keeps it, but hands it to someone else — a contracting
 * officer, an engineer, legal — named in referredTo.
 */
export interface Disposition {
  findingId: string;
  decision: 'include' | 'not-a-concern' | 'refer';
  /** Set on 'not-a-concern'. */
  reason?: DismissReason;
  /** Free text, required by the UI when reason is 'other'. */
  reasonText?: string;
  /** The reviewer's own note, kept whatever the decision. */
  note?: string;
  /** Set on 'refer': who it went to. */
  referredTo?: string;
  decidedAt: string;
}

/**
 * Type of Submittal/Deliverable on the P3-09 form. The labels are in
 * src/data/review.ts; these are the stored values.
 */
export type SubmittalType =
  | 'preliminary-assessment'
  | 'iga-midpoint-review'
  | 'proposal-iga'
  | 'commissioning-plan'
  | 'commissioning-report'
  | 'post-installation-report'
  | 'year-1-annual-report'
  | 'other';

/**
 * The header block of the FEMP ESPC Project Review Comment Template,
 * Process Doc P3-09 rev 09/2025. See docs/redesign/P3-09.md. Field for field,
 * in the order the form prints them. Empty strings, never undefined: the form
 * has these boxes whether or not the reviewer has filled them.
 */
export interface HeaderFields {
  /** "Project Name/No." */
  projectName: string;
  /** "Contract No." */
  contractNo: string;
  /** "Date of Document" */
  documentDate: string;
  /** "Comments Due By" */
  commentsDueBy: string;
  submittalType: SubmittalType;
  /** The "Other (ID)" text, used only when submittalType is 'other'. */
  submittalOther: string;
  reviewerName: string;
  reviewerDate: string;
  reviewerPhone: string;
  reviewerEmail: string;
}

/** The evidence-strength filter above the findings list. Not a grouping. */
export type EvidenceFilter =
  | 'all'
  | 'arithmetic'
  | 'audit-precedent'
  | 'guidance-warning';

/**
 * The decision filter above it. The labels, and the reason there is no
 * "asked an SME" option, are in src/routes/workspaceModel.ts.
 */
export type DecisionFilter = 'all' | 'to-decide' | 'included' | 'not-a-concern';

/**
 * Where the reviewer had got to in the workspace. Restored on resume so that
 * reopening a review does not throw away the reading position, but never
 * load-bearing: a review with a paneState pointing at a finding that no longer
 * exists must still open.
 *
 * Whether the document pane is open is deliberately not here. That is a fact
 * about how this reviewer works rather than about this document, so it is a
 * browser-level preference — `UiPrefs` in src/data/store.ts — and there is
 * exactly one home for it.
 */
export interface PaneState {
  selectedFindingId: string | null;
  /** The page the document pane is showing, 1-based. */
  documentPage: number | null;
  evidenceFilter: EvidenceFilter;
  /** Which decisions the findings list is showing. */
  decisionFilter: DecisionFilter;
}

/**
 * One review of one document. This is the product's output: what the reviewer
 * decided, and the comments they will send.
 *
 * dispositions and editedComments are keyed by finding id. A finding with no
 * entry in dispositions has not been decided on yet; a finding with no entry in
 * editedComments still has its generated draft, which is never overwritten in
 * place — the draft and the reviewer's edit of it are kept apart so the edit
 * can be reverted.
 */
export interface ReviewRecord {
  docId: string;
  fileName: string;
  headerFields: HeaderFields;
  dispositions: Record<string, Disposition>;
  editedComments: Record<string, string>;
  /**
   * Checks the reviewer chose to leave unanswered, by check id. A check that
   * needs a confirmation before it can run offers "Skip for now"; pressing it
   * is a decision about this review, so it is recorded here rather than held in
   * the page. Always reversible: a set-aside check stays in the list, in the
   * group for checks that did not run, and can be answered later.
   */
  skippedChecks: string[];
  paneState: PaneState;
  createdAt: string;
  updatedAt: string;
}

/**
 * A check the reviewer has quieted, because the same reason for setting it
 * aside came up often enough that continuing to raise it is noise. Per browser
 * rather than per review: it is a fact about how this reviewer works, not about
 * one document. Muting hides a check's findings from the list; it never stops
 * the check running, and the muted set is always visible and reversible.
 */
export interface MutedCheck {
  failureModeId: FailureModeId;
  /** The dismissal reason that recurred. */
  reason: DismissReason;
  /** How many dismissals with that reason triggered the mute. */
  count: number;
  mutedAt: string;
  /**
   * Where the mute came from. Absent on records written before /checks could
   * mute a check directly, which were all offers accepted inside a review, so
   * absence reads as 'dismissals'. It changes nothing about what muting does;
   * it only decides which sentence the check tells about itself, because
   * "you set this aside three times" is false of one switched off in advance.
   */
  source?: 'dismissals' | 'reference';
}

/** Keyed by failure mode id. */
export type MutedChecks = Partial<Record<FailureModeId, MutedCheck>>;

/**
 * Checks the reviewer was offered the chance to mute and asked to keep seeing,
 * keyed by failure mode *and* dismissal reason — `mutePromptKey()` in
 * src/data/review.ts — with the dismissal count at which they said so.
 *
 * "Keep showing it" is an answer rather than a postponement, so it is recorded
 * for the same reason a dismissal reason is: a reviewer should not be asked the
 * same question twice about the same three decisions. It is per browser, like
 * the muted set, and the offer returns only if the same reason comes up again
 * afterwards.
 */
export type MutePrompts = Record<string, number>;

/**
 * A document's page text, stored apart from the SourceDocument so the document
 * pane can open a page without reading the whole parse — rawText, sections,
 * tables and all — back out of storage.
 */
export interface ExtractedPages {
  docId: string;
  pageCount: number;
  pages: { number: number; text: string }[];
  extractedAt: string;
}

/** The findings of one run against one document. */
export interface FindingsRecord {
  docId: string;
  createdAt: string;
  flags: Flag[];
  /** Check ids that ran to completion, for the report header. */
  ranChecks: string[];
  /**
   * Checks that did not run, and why. Stored rather than recomputed so that a
   * reviewer returning to a review still learns which checks stayed silent —
   * a check that produced nothing because it never ran must never look like a
   * check that ran and found nothing.
   */
  skipped?: CheckSkip[];
  /**
   * The tables the reviewer confirmed, when this run included the table
   * reconciliation check. Stored with the findings because it is part of how
   * they were produced, and because answering one further question re-runs the
   * check and must not throw away the answers already given.
   */
  reconcileSelection?: ReconcileSelection;
}

/* ==========================================================================
   Completeness
   --------------------------------------------------------------------------
   The §2.2.4 checklist emits gaps rather than failure-mode flags: a missing
   required section is a gap in the document, not yet a dispute cause.
   ========================================================================== */

export type CompletenessStatus = 'present' | 'absent' | 'uncertain';

export interface CompletenessItem {
  id: string;
  scope: 'project' | 'ecm';
  /** The requirement as FEMP states it. */
  label: string;
  /** Which part of §2.2.4 this comes from. */
  reference: string;
}

export interface CompletenessGap {
  id: string;
  checkId: string;
  itemId: string;
  scope: 'project' | 'ecm';
  label: string;
  reference: string;
  status: CompletenessStatus;
  /** Which ECM this applies to, for ECM-level items. */
  ecmRef?: string;
  /** Present only where something was found; absence has nothing to quote. */
  quote: string | null;
  page: number | null;
  receipt: Receipt;
}

/* ==========================================================================
   Run result
   ========================================================================== */

/** A check that threw. Reported, never allowed to kill the run. */
export interface CheckFailure {
  checkId: string;
  message: string;
}

/** A check that did not run, and why. Skipping is reported, never silent. */
export interface CheckSkip {
  checkId: string;
  reason: string;
  /**
   * What the check needed and where it went looking for it, shown behind
   * "What it looked for" on the row that reports the skip. A reviewer told
   * only that a check did not run cannot tell whether that is a fact about
   * their document or a fact about this tool; this is how they find out.
   */
  lookedFor?: string;
}

export interface DeterministicResult {
  flags: Flag[];
  gaps: CompletenessGap[];
  failures: CheckFailure[];
  /** Checks that ran to completion, for the report header. */
  ranChecks: string[];
  skipped: CheckSkip[];
}
